<template>
  <v-container fluid>
    <div class="d-flex flex-column flex-md-row justify-space-between">
      <div>
        <h1>{{ $t('PROFILE_PAGE.TITLE.HEADER') }}</h1>
        <span>{{ $t('PROFILE_PAGE.TITLE.SUBTITLE') }}</span>
      </div>
      <div class="d-flex align-center justify-md-center justify-start mt-8 mt-md-0">
        <v-btn class="boom-button-back" variant="outlined" to="/dashboard">
          {{ $t('COMMON.BUTTON.BACK') }}
        </v-btn>
      </div>
    </div>
    <v-row>
      <v-col cols="12" md="6" class="d-flex">
        <v-card class="rounded-xl mt-8 flex-fill" :border="false" :elevation="0">
          <v-form
            class="fill-height"
            ref="formProfile"
            v-model="formProfileValid"
            @submit.prevent="submitUpdateAccount"
          >
            <v-container class="v-container fill-height ma-0 pa-0">
              <v-row class="align-self-start w-100">
                <v-col cols="12">
                  <h3 class="font-weight-bold">{{ $t('PROFILE_PAGE.FORM.PERSONAL_INFO') }}</h3>
                </v-col>
                <v-col cols="12" md="6">
                  <p>{{ $t('PROFILE_PAGE.FORM.LABEL_PHONE') }}</p>
                  <v-phone-input
                    v-model="phone"
                    v-model:country="selectedCountry"
                    :includeCountries="allCountries"
                    :rules="requiredRules"
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <p>{{ $t('PROFILE_PAGE.FORM.LABEL_EMAIL_LOGIN') }}</p>
                  <v-text-field
                    v-model="email"
                    :rules="[...requiredRules, ...emailRules]"
                    disabled
                    required
                    type="email"
                  />
                </v-col>
              </v-row>
              <v-row class="align-self-end">
                <v-col cols="12" class="d-flex justify-end">
                  <v-btn
                    class="boom-button"
                    variant="outlined"
                    :disabled="!formProfileValid || loading"
                    :loading="loading"
                    type="submit"
                  >
                    {{ $t('PROFILE_PAGE.BUTTON.VALIDATE') }}
                  </v-btn>
                </v-col>
              </v-row>
            </v-container>
          </v-form>
        </v-card>
      </v-col>
      <v-col cols="12" md="6" class="d-flex">
        <v-card class="rounded-xl mt-8 flex-fill" :border="false" :elevation="0">
          <v-form
            class="fill-height"
            ref="formPassword"
            v-model="formPasswordValid"
            @submit.prevent="submitUpdatePassword"
          >
            <v-container class="v-container fill-height ma-0 pa-0">
              <v-row class="align-self-start w-100">
                <v-col cols="12">
                  <h3 class="font-weight-bold">{{ $t('PROFILE_PAGE.FORM.CONNECTION_INFO') }}</h3>
                </v-col>
                <v-col cols="12">
                  <p>{{ $t('PROFILE_PAGE.FORM.LABEL_CURRENT_PASSWORD') }}</p>
                  <v-text-field
                    v-model="password"
                    :append-inner-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
                    :rules="requiredRules"
                    :type="showPassword ? 'text' : 'password'"
                    @click:append-inner="showPassword = !showPassword"
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <p>{{ $t('PROFILE_PAGE.FORM.LABEL_NEW_PASSWORD') }}</p>
                  <v-text-field
                    v-model="newPassword"
                    :append-inner-icon="showNewPassword ? 'mdi-eye' : 'mdi-eye-off'"
                    :rules="[...requiredRules, ...passwordRules]"
                    :type="showNewPassword ? 'text' : 'password'"
                    @click:append-inner="showNewPassword = !showNewPassword"
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <p>{{ $t('PROFILE_PAGE.FORM.LABEL_CONFIRM_PASSWORD') }}</p>
                  <v-text-field
                    v-model="confirmPassword"
                    :append-inner-icon="showConfirmPassword ? 'mdi-eye' : 'mdi-eye-off'"
                    :rules="[...requiredRules, ...passwordRules, ...confirmPasswordRules]"
                    :type="showConfirmPassword ? 'text' : 'password'"
                    @click:append-inner="showConfirmPassword = !showConfirmPassword"
                  />
                </v-col>
              </v-row>
              <v-row class="align-self-end">
                <v-col cols="12" class="d-flex justify-end">
                  <v-btn
                    class="boom-button"
                    variant="outlined"
                    :disabled="!formPasswordValid || loading"
                    :loading="loading"
                    type="submit"
                  >
                    {{ $t('PROFILE_PAGE.BUTTON.VALIDATE_PASSWORD') }}
                  </v-btn>
                </v-col>
              </v-row>
            </v-container>
          </v-form>
        </v-card>
      </v-col>
    </v-row>
    <v-card class="rounded-xl mt-8" :border="false" :elevation="0">
      <h3 class="font-weight-bold mb-4">{{ $t('PROFILE_PAGE.TABLE.COLLABORATORS') }}</h3>
      <v-row>
        <v-col cols="12" lg="10">
          <v-text-field v-model="search" placeholder="Rechercher" hide-details />
        </v-col>
        <v-col cols="12" lg="2" class="d-flex justify-end">
          <v-btn
            v-if="
              checkPermission(['DISTRIBUTOR_ADD_COLLABORATOR']) &&
              checkHasSeveralCollaboratorsPermission()
            "
            class="boom-button"
            variant="outlined"
            :loading="loading"
            :to="{ name: 'ProfileCreateCollaborator' }"
          >
            {{ $t('PROFILE_PAGE.BUTTON.ADD_COLLABORATOR') }}
          </v-btn>
        </v-col>
      </v-row>
      <v-table :hover="true" class="mt-8">
        <thead>
          <tr>
            <th id="lastname" @click="sortColumns('lastname')">
              <div class="d-flex align-center justify-space-between">
                <span class="font-weight-bold">{{ $t('PROFILE_PAGE.TABLE.LASTNAME') }}</span>
                <SortArrow :order="order" :sort="sort" value="lastname" />
              </div>
            </th>
            <th id="firstname" @click="sortColumns('firstname')">
              <div class="d-flex align-center justify-space-between">
                <span class="font-weight-bold">{{ $t('PROFILE_PAGE.TABLE.FIRSTNAME') }}</span>
                <SortArrow :order="order" :sort="sort" value="firstname" />
              </div>
            </th>
            <th id="job_description" @click="sortColumns('job_description')">
              <div class="d-flex align-center justify-space-between">
                <span class="font-weight-bold">{{ $t('PROFILE_PAGE.TABLE.JOB_DESCRIPTION') }}</span>
                <SortArrow :order="order" :sort="sort" value="job_description" />
              </div>
            </th>
            <th
              v-if="checkPermission(['DISTRIBUTOR_UPDATE_COLLABORATOR'])"
              id="actions"
              style="width: 60px"
            />
          </tr>
        </thead>
        <tbody>
          <tr v-if="loading">
            <td class="pa-8 pa-md-16 text-md-center" colspan="4">
              {{ $t('PROFILE_PAGE.TABLE.LOADING_COLLABORATOR') }}
              <v-progress-linear class="my-5" color="#165039" indeterminate />
            </td>
          </tr>
          <tr v-else-if="!collaborators.length">
            <td class="pa-8 pa-md-16 text-md-center" colspan="4">
              {{ $t('PROFILE_PAGE.TABLE.NO_COLLABORATOR') }}
              <div class="d-flex align-center justify-center mt-8">
                <img src="@/assets/images/empty.svg" width="250" alt="Empty" />
              </div>
            </td>
          </tr>
          <tr
            v-for="collaborator of collaborators"
            v-else
            :key="collaborator.technical_id"
            @click.stop.prevent="updateCollaborator(collaborator)"
          >
            <td class="font-weight-bold">
              {{ collaborator.lastname }}
            </td>
            <td class="font-weight-bold">
              {{ collaborator.firstname }}
            </td>
            <td class="color-light">
              {{
                collaborator.job_description
                  ? collaborator.job_description
                  : $t('PROFILE_PAGE.TABLE.NO_JOB_DESCRIPTION')
              }}
            </td>
            <td
              v-if="checkPermission(['DISTRIBUTOR_UPDATE_COLLABORATOR'])"
              class="text-right"
              @click.stop.prevent
            >
              <v-menu>
                <template v-slot:activator="{ props }">
                  <svg-icon
                    v-bind="props"
                    @click.stop.prevent
                    class="icon"
                    type="mdi"
                    :path="dotVertical"
                  />
                </template>
                <v-list elevation="1">
                  <v-list-item
                    link
                    :to="'/profile/edit-collaborator/' + `${collaborator.technical_id}`"
                  >
                    <v-list-item-title>
                      {{ $t('PROFILE_PAGE.BUTTON.UPDATE_COLLABORATOR') }}
                    </v-list-item-title>
                  </v-list-item>
                  <v-list-item
                    v-if="getCurrentCollaborator.technical_id !== collaborator.technical_id"
                  >
                    <v-list-item-title @click="disableCollaborator(collaborator)">
                      {{ $t('COMMON.BUTTON.DELETE') }}
                    </v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </td>
          </tr>
        </tbody>
      </v-table>
      <div v-if="collaborators.length" class="mt-8 d-flex align-center justify-center">
        <p class="load-more color-light" @click="loadMore()">{{ $t('COMMON.BUTTON.LOAD_MORE') }}</p>
      </div>
    </v-card>
    <ConfirmDialogue ref="confirmDialogue" />
  </v-container>
</template>

<script>
import { displayToast, handleResponseError } from '@/components/utils/globalFunction'
import { auth as $store } from '@/store/AuthModule'
import Validator from '@/components/utils/validator'
import { mdiDotsVertical } from '@mdi/js'
import SortArrow from '@/components/tables/SortArrow.vue'
import SvgIcon from '@jamescoyle/vue-icon'
import CollaboratorService from '@/services/CollaboratorService'
import RegionCodeService from '@/services/RegionCodeService'
import { VPhoneInput } from 'v-phone-input'
import ResponseConstant from '@/constants/responseConstant'
import ConfirmDialogue from '@/components/modal/ConfirmDialogue.vue'
import collaboratorStatus from '@/constants/collaboratorStatus'
import AccountService from '@/services/AccountService'

export default {
  name: 'ProfilePage',
  components: { ConfirmDialogue, VPhoneInput, SortArrow, SvgIcon },
  data() {
    return {
      selectedCollaborator: null,
      showDialog: false,
      loading: false,
      formProfileValid: false,
      formPasswordValid: false,
      phone: '',
      email: null,
      selectedCountry: null,
      password: '',
      newPassword: '',
      confirmPassword: '',
      showPassword: false,
      showNewPassword: false,
      showConfirmPassword: false,
      collaborators: [],
      allCountries: [],
      dotVertical: mdiDotsVertical,
      search: '',
      sort: 'lastname',
      order: 'asc',
      page: 1,
      pages: null,
      limit: 12,
      rulesEmpty: [],

      requiredRules: Validator.requiredRules,
      emailRules: Validator.emailRules,
      passwordRules: Validator.passwordRules
    }
  },
  async created() {
    if (!$store.state.userDetail.collaboratorList.length) {
      const collaborator = await JSON.parse(localStorage.getItem('collaborator'))
      $store.mutations.meSuccess($store.state, collaborator)
    }
    try {
      const response = await RegionCodeService.getRegionCodeList()
      this.allCountries = this.getCountryCode(response.item_list)
    } catch (error) {
      handleResponseError(error)
    }
  },
  mounted() {
    this.patchUser()
    this.getAccountCollaboratorsList()
  },
  watch: {
    search() {
      if (!this.awaitingSearch) {
        setTimeout(() => {
          this.getAccountCollaboratorsList()
          this.awaitingSearch = false
        }, 1000)
      }
      this.awaitingSearch = true
    }
  },
  computed: {
    getCurrentCollaborator() {
      const collaborator = this.$store.getters['auth/getCurrentCollaborator']
      if (collaborator) {
        return collaborator
      } else {
        return ''
      }
    },
    confirmPasswordRules() {
      return Validator.confirmPasswordRule(this.password)
    }
  },
  methods: {
    checkPermission(permissionsRequired) {
      return this.$store.getters['auth/hasPermission'](permissionsRequired)
    },
    checkHasSeveralCollaboratorsPermission() {
      return this.$store.getters['auth/hasSeveralCollaborators']
    },
    getCountryCode(zones) {
      return zones.map((item) => {
        return item.country_code
      })
    },
    patchUser() {
      const collaborator = $store.state.userDetail
      this.phone = collaborator.phoneNumber
      this.email = collaborator.email
    },
    setError(key) {
      switch (key) {
        case 'invalid_phone_number_error_key':
          this.error = { phone: this.$t('ERROR.' + ResponseConstant[key]) }
          break
        case 'invalid_password_error_key':
          this.error = { password: this.$t('ERROR.' + ResponseConstant[key]) }
          break
        case 'invalid_new_confirm_password_error_key':
          this.error = { newPassword: this.$t('ERROR.' + ResponseConstant[key]) }
          break
        default:
          this.error = ''
      }
    },
    async disableCollaborator(collaborator) {
      const confirm = await this.$refs.confirmDialogue.show({
        title: this.$t('PROFILE_PAGE.DIALOGUE.TITLE'),
        message: this.$t('PROFILE_PAGE.DIALOGUE.MESSAGE', {
          collaborator: collaborator.firstname + ' ' + collaborator.lastname
        }),
        warning: this.$t('PROFILE_PAGE.DIALOGUE.WARNING'),
        validateButton: this.$t('COMMON.BUTTON.DELETE'),
        cancelButton: this.$t('COMMON.BUTTON.CANCEL')
      })
      if (confirm) {
        collaborator.status = collaboratorStatus.inactive.name
        try {
          const response = await CollaboratorService.updateCollaborator(collaborator)
          displayToast(this.$t('PROFILE_PAGE.MESSAGE.SUCCESS_INACTIVE'), 'success')
          await this.$store.commit('auth/updateOneCollaboratorsInList', response)
          await this.getAccountCollaboratorsList()
        } catch (error) {
          handleResponseError(error)
        } finally {
          this.showDialog = false
          this.loading = false
        }
      }
    },
    async getAccountCollaboratorsList() {
      this.loading = true
      try {
        const response = await CollaboratorService.getPaginatedAccountCollaboratorsList(
          this.search,
          this.sort,
          this.order,
          this.page,
          this.limit
        )
        this.collaborators = response.data
        this.pages = response.max_page
      } catch (error) {
        handleResponseError(error)
      } finally {
        this.loading = false
      }
    },
    sortColumns(sortParam) {
      this.page = 1
      this.sort = sortParam
      if (this.order === 'asc') {
        this.order = 'desc'
      } else {
        this.order = 'asc'
      }
      this.getAccountCollaboratorsList()
    },
    updateCollaborator(collaborator) {
      if (this.checkPermission(['DISTRIBUTOR_UPDATE_COLLABORATOR'])) {
        this.$router.push('/profile/edit-collaborator/' + collaborator.technical_id)
      }
    },
    async loadMore() {
      if (this.page < this.pages) {
        this.page = this.page + 1
        const response = await CollaboratorService.getPaginatedAccountCollaboratorsList(
          this.search,
          this.sort,
          this.order,
          this.page,
          this.limit
        )
        this.collaborators.push(...response.data)
      }
    },
    async submitUpdateAccount() {
      const data = {
        phone_number: this.phone
      }
      try {
        const response = await AccountService.updateAccountProfile(data)
        await this.$store.commit('auth/meSuccess', response)
        displayToast(this.$t('PROFILE_PAGE.MESSAGE.SUCCESS'), 'success')
        this.$router.push({ name: 'Profile' })
      } catch (error) {
        handleResponseError(error)
      } finally {
        this.loading = false
      }
    },
    async submitUpdatePassword() {
      const data = {
        password: this.password,
        new_password: this.newPassword,
        confirm_new_password: this.confirmPassword
      }
      try {
        await AccountService.updateAccountPassword(data)
        displayToast(this.$t('PROFILE_PAGE.MESSAGE.SUCCESS'), 'success')
        this.$router.push({ name: 'Profile' })
      } catch (error) {
        handleResponseError(error)
        if (error.response?.data && error.response.data.key in ResponseConstant) {
          this.setError(error.response.data.key)
        }
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.v-container {
  padding: 32px;
}

.v-card {
  padding: 32px;
}

.v-form p {
  margin-bottom: 16px;
}

.v-form p:after {
  content: ' *';
  color: var(--error-color);
}

:deep(.v-table__wrapper) {
  border-radius: 20px !important;
}

thead {
  background-color: #165039 !important;
}

th > * {
  color: white;
}

tr:nth-of-type(even) {
  background-color: #fafafa;
}

td {
  border-bottom: unset !important;
}

td:hover {
  cursor: pointer;
}

.color-light {
  color: #bfbfbf;
}

.load-more {
  cursor: pointer;
}

:deep(.v-field__append-inner .v-icon) {
  color: lightgrey !important;
}
</style>
